// Data-driven sub-region config. Add a market key + regions here; no new page code needed.
// Any region not listed falls back to the market's `default` block, so a new region needs
// only a small row. `list` powers the linking grid on the country page; `regions` holds the
// detailed content, and `default` covers everything else in the list.

const CA_IMAGE = 'https://images.unsplash.com/photo-1486325212027-8081e485255e?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000';
const UAE_IMAGE = 'https://images.unsplash.com/photo-1634007626524-f47fa37810a7?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000';
const US_IMAGE = 'https://images.unsplash.com/photo-1514565131-fce0801e5785?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000';
const UK_IMAGE = 'https://images.unsplash.com/photo-1712873068978-eb0cb20d1b62?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000';
const SG_IMAGE = 'https://images.unsplash.com/photo-1727859146476-c60c7f8f9764?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000';

export const REGION_CONFIG = {
  canada: {
    label: 'Canada', kind: 'Province', image: CA_IMAGE,
    basePath: '/country/canada/provinces', countryPath: '/country/canada',
    intro: 'In Canada the province you choose shapes director-residency options, your combined tax rate and where you file. Here is how the main provinces compare for founders.',
    linkHeading: { eyebrow: 'Where to incorporate', title: 'Compare the Canadian provinces.', text: 'Your province shapes director-residency options, combined tax rates and where you file. Explore the main provinces international founders choose.' },
    guide: {
      process: [
        { title: 'Federal or provincial', text: 'Decide between federal (CBCA) incorporation and the province, based on where you operate and each option’s director-residency rules.' },
        { title: 'Name & NUANS', text: 'Choose a corporate name and run a NUANS name search, or opt for a numbered company to move faster.' },
        { title: 'Incorporate', text: 'File articles of incorporation with the chosen registry and set up your directors and share structure.' },
        { title: 'Registrations', text: 'Obtain a Business Number and register for GST/HST, payroll and any provincial accounts you need.' },
        { title: 'First filings', text: 'Set up your minute book and diarise the annual return and the T2 corporate income tax return.' },
      ],
      essentialsTitle: 'What you will need',
      essentials: [
        'Directors’ details — check the province’s residency rule',
        'A Canadian registered office address',
        'A corporate name and NUANS report, or a numbered company',
        'Your share structure and shareholder details',
      ],
      supportTitle: 'How StartEase helps',
      support: [
        'Advising on federal vs provincial and director-residency options',
        'Incorporating and preparing your minute book',
        'Business Number and GST/HST registration',
        'T2 corporate tax and annual return filing',
      ],
      costsNote: 'Costs depend on federal vs provincial filing, the name search, and whether you need a resident-director solution. We provide a transparent, all-in estimate before filing.',
      faqs: [
        { question: 'Federal or provincial — which should I choose?', answer: 'Federal incorporation gives national name protection but requires 25% resident-Canadian directors; provincial incorporation is simpler if you operate in one province, and some provinces have no residency requirement.' },
        { question: 'Do I need a resident Canadian director?', answer: 'Federal incorporation and some provinces require a proportion of resident-Canadian directors. Provinces such as Ontario and British Columbia have removed this requirement.' },
      ],
    },
    regions: {
      ontario: {
        name: 'Ontario',
        tagline: "Canada's largest market, with no director-residency requirement.",
        overview: "Ontario is home to Toronto and the largest provincial economy in Canada. It removed its director-residency requirement, so non-resident founders can incorporate provincially here without a resident Canadian director — a common reason to choose Ontario over federal incorporation.",
        facts: [
          { label: 'Director residency', value: 'No requirement' },
          { label: 'Incorporation', value: 'Provincial (OBCA) or federal (CBCA)' },
          { label: 'Sales tax', value: 'HST 13%' },
          { label: 'Registry', value: 'Ontario Business Registry' },
        ],
        faqs: [
          { question: 'Can a non-resident incorporate in Ontario?', answer: 'Yes. Ontario has no director-residency requirement, so a non-resident can incorporate provincially without a resident Canadian director. You still need an Ontario registered office and must keep corporate records.' },
          { question: 'What sales tax applies in Ontario?', answer: 'Ontario uses the Harmonized Sales Tax (HST) at 13%, combining the federal GST and the provincial portion. Registration is generally required once worldwide taxable revenue exceeds CAD 30,000 over four consecutive quarters.' },
        ],
      },
      'british-columbia': {
        name: 'British Columbia',
        tagline: 'West-coast base, no director-residency rule, PST separate from GST.',
        overview: "British Columbia (Vancouver) is a popular choice for non-resident founders because it has no director-residency requirement. Note that BC charges a provincial sales tax (PST) separately from the federal GST, rather than a harmonized HST.",
        facts: [
          { label: 'Director residency', value: 'No requirement' },
          { label: 'Incorporation', value: 'Provincial (BCBCA) or federal (CBCA)' },
          { label: 'Sales tax', value: 'GST 5% + PST 7%' },
          { label: 'Registry', value: 'BC Registries' },
        ],
        faqs: [
          { question: 'Why do non-residents choose British Columbia?', answer: 'BC has no director-residency requirement, making it practical for founders who cannot meet the federal 25% resident-director rule. Vancouver also offers strong links to Asia-Pacific markets.' },
          { question: 'How is sales tax handled in BC?', answer: 'BC applies the federal GST at 5% plus a separate provincial sales tax (PST) at 7%, rather than a single harmonized rate. The two are administered separately.' },
        ],
      },
      alberta: {
        name: 'Alberta',
        tagline: 'Low tax, no PST, but a director-residency requirement applies.',
        overview: "Alberta (Calgary, Edmonton) is known for a business-friendly, low-tax environment and no provincial sales tax. Unlike Ontario and BC, Alberta generally requires resident-Canadian directors for provincial incorporation, so non-residents often incorporate federally or choose another province.",
        facts: [
          { label: 'Director residency', value: 'Resident directors generally required' },
          { label: 'Incorporation', value: 'Provincial (ABCA) or federal (CBCA)' },
          { label: 'Sales tax', value: 'GST 5% (no PST)' },
          { label: 'Registry', value: 'Alberta Corporate Registry' },
        ],
        faqs: [
          { question: 'Can a non-resident incorporate provincially in Alberta?', answer: 'Alberta generally requires a proportion of resident-Canadian directors for provincial incorporation, so a non-resident without a resident co-director usually incorporates federally or picks a no-residency province like BC or Ontario.' },
          { question: 'Does Alberta have a provincial sales tax?', answer: 'No. Alberta charges only the federal GST at 5% and has no provincial sales tax, which is part of its low-tax appeal.' },
        ],
      },
      quebec: {
        name: 'Quebec',
        tagline: 'Large market with French-language obligations and QST.',
        overview: "Quebec (Montreal) is a major market, but it has specific French-language requirements for business names and operations, and it administers its own Quebec Sales Tax (QST) alongside the federal GST. Factor the language and QST obligations into your planning.",
        facts: [
          { label: 'Director residency', value: 'No general residency requirement' },
          { label: 'Incorporation', value: 'Provincial (QBCA) or federal (CBCA)' },
          { label: 'Sales tax', value: 'GST 5% + QST 9.975%' },
          { label: 'Note', value: 'French-language obligations apply' },
        ],
        faqs: [
          { question: 'What is special about incorporating in Quebec?', answer: 'Quebec has French-language requirements for company names and certain operations, and administers its own Quebec Sales Tax (QST) in addition to the federal GST. Plan for both when operating there.' },
        ],
      },
    },
    list: [
      { slug: 'ontario', name: 'Ontario', code: 'ON', blurb: 'Largest market, no residency rule' },
      { slug: 'british-columbia', name: 'British Columbia', code: 'BC', blurb: 'West coast, no residency rule' },
      { slug: 'alberta', name: 'Alberta', code: 'AB', blurb: 'Low tax, no PST' },
      { slug: 'quebec', name: 'Quebec', code: 'QC', blurb: 'Large market, QST & French rules' },
      { slug: 'manitoba', name: 'Manitoba', code: 'MB', blurb: 'Central Canada' },
      { slug: 'saskatchewan', name: 'Saskatchewan', code: 'SK', blurb: 'Prairie economy' },
      { slug: 'nova-scotia', name: 'Nova Scotia', code: 'NS', blurb: 'Atlantic Canada' },
      { slug: 'new-brunswick', name: 'New Brunswick', code: 'NB', blurb: 'Atlantic Canada' },
    ],
    default: {
      overview: "This province follows the general Canadian framework: you can incorporate provincially under its own statute or federally under the CBCA. Corporate tax combines a federal and a provincial rate, GST/HST applies once you cross the CAD 30,000 threshold, and the company files a T2 return. Director-residency rules vary by province, so confirm before you file.",
      facts: [
        { label: 'Incorporation', value: 'Provincial or federal (CBCA)' },
        { label: 'Corporate tax', value: 'Federal + provincial rate' },
        { label: 'Sales tax', value: 'GST/HST (province-dependent)' },
        { label: 'Return', value: 'T2 corporate income tax' },
      ],
      faqs: [
        { question: 'Should I incorporate provincially or federally in this province?', answer: 'Federal incorporation gives national name protection but requires 25% resident-Canadian directors; provincial incorporation is simpler if you operate mainly in one province. Some provinces have no residency requirement — confirm this one before filing.' },
        { question: 'When must I register for GST/HST?', answer: 'Generally once worldwide taxable revenue exceeds CAD 30,000 over four consecutive quarters. The rate and whether it is GST, HST or GST+PST depends on the province.' },
      ],
    },
  },

  uae: {
    label: 'United Arab Emirates', kind: 'Free zone', image: UAE_IMAGE,
    basePath: '/country/uae/free-zones', countryPath: '/country/uae',
    intro: 'The UAE has more than 40 free zones, each with its own activities, costs and reputation. Here is how the free zones international founders ask about most often compare.',
    linkHeading: { eyebrow: 'Where to set up', title: 'Choose the right UAE free zone.', text: 'Each free zone suits different activities, budgets and ambitions — from low-cost consultancy licences to premium financial and trading hubs. Compare the options founders choose most.' },
    guide: {
      process: [
        { title: 'Zone & activities', text: 'Confirm the free zone and the exact business activities on your licence — this drives cost, visa quota and whether you can serve the mainland or only export and operate internationally.' },
        { title: 'Name & application', text: 'Reserve your company name, submit shareholder and passport documents and file the licence application with the free-zone authority.' },
        { title: 'Licence & card', text: 'Receive your trade licence, incorporation certificate and establishment card, and set up the flexi-desk or office your activity requires.' },
        { title: 'Visas & Emirates ID', text: 'Apply for investor and employee residence visas and Emirates IDs under your visa quota, with medical and biometrics steps in the UAE.' },
        { title: 'Bank & tax', text: 'Open a corporate bank account, then register for Corporate Tax and, if turnover requires it, VAT.' },
      ],
      essentialsTitle: 'What you will need',
      essentials: [
        'Passport copies for every shareholder and director',
        'A clear list of intended business activities',
        'Proof of address, and a short business plan for some zones',
        'A UAE address — a flexi-desk, or a physical office for higher visa counts',
      ],
      supportTitle: 'How StartEase helps',
      support: [
        'Advising on the right free zone for your activity and budget',
        'Preparing and filing the licence application and documents',
        'Coordinating your establishment card, visas and Emirates ID',
        'Corporate Tax and VAT registration and ongoing filing',
      ],
      costsNote: 'Free-zone costs vary widely — a lean consultancy licence on a flexi-desk sits at the affordable end, while premium zones, extra visas and physical offices raise the total. We give you an itemised, all-in estimate before you commit.',
      faqs: [
        { question: 'How long does a UAE free zone setup take?', answer: 'Many free zones issue a licence within a few working days once documents are in order; visas and banking typically add one to three weeks depending on the zone and your nationality.' },
        { question: 'Do I need to be in the UAE to set up?', answer: 'A large part of the process can be handled remotely, but most residence-visa steps — medical, biometrics and Emirates ID — require a short visit to the UAE.' },
      ],
    },
    regions: {
      ifza: {
        name: 'IFZA',
        tagline: 'A flexible, cost-effective Dubai free zone popular with consultants and small trading companies.',
        overview: 'IFZA (International Free Zone Authority) operates from Dubai and is known for competitive package pricing, a wide list of permitted activities and 100% foreign ownership. It suits founders who want a Dubai licence without leasing a physical office, since a flexi-desk arrangement is usually enough to get started.',
        facts: [
          { label: 'Ownership', value: '100% foreign' },
          { label: 'Best for', value: 'Consultancy, services, trading' },
          { label: 'Office', value: 'Flexi-desk accepted' },
          { label: 'Location', value: 'Dubai' },
        ],
        faqs: [
          { question: 'Do I need a physical office to set up with IFZA?', answer: 'No. A flexi-desk package is generally sufficient for an IFZA licence, which keeps first-year costs low. A physical office is optional and depends on your activity and visa needs.' },
          { question: 'Can a foreigner own 100% of an IFZA company?', answer: 'Yes. Like other UAE free zones, IFZA allows full foreign ownership with no local sponsor required.' },
        ],
      },
      meydan: {
        name: 'Meydan Free Zone',
        tagline: 'A prestigious Dubai address with fast, streamlined setup.',
        overview: 'Meydan Free Zone offers a well-regarded Dubai address near Downtown and is popular for e-commerce, trading and professional services. It is known for a straightforward, quick incorporation process and 100% foreign ownership, with flexi-desk options that keep entry costs manageable.',
        facts: [
          { label: 'Ownership', value: '100% foreign' },
          { label: 'Best for', value: 'E-commerce, trading, services' },
          { label: 'Office', value: 'Flexi-desk accepted' },
          { label: 'Location', value: 'Dubai' },
        ],
        faqs: [
          { question: 'Why do founders choose Meydan Free Zone?', answer: 'Meydan is valued for its central Dubai address, quick setup process and broad activity list, making it popular with e-commerce and service businesses that want a credible Dubai base.' },
        ],
      },
      dmcc: {
        name: 'DMCC',
        tagline: "One of the world's most awarded free zones, a global hub for trade and commodities.",
        overview: 'The Dubai Multi Commodities Centre (DMCC), based in Jumeirah Lakes Towers, hosts a large community of trading, commodities, precious-metals, fintech and crypto businesses. It carries a more premium price point but offers strong credibility, an established business district and mature banking relationships.',
        facts: [
          { label: 'Ownership', value: '100% foreign' },
          { label: 'Best for', value: 'Trading, commodities, crypto' },
          { label: 'Profile', value: 'Premium, prestigious' },
          { label: 'Location', value: 'Dubai (JLT)' },
        ],
        faqs: [
          { question: 'Is DMCC more expensive than other free zones?', answer: 'DMCC sits at the premium end of UAE free zones. Founders choose it for its global reputation, physical business district and strong banking ecosystem rather than the lowest price.' },
          { question: 'What businesses are based in DMCC?', answer: 'DMCC is a hub for commodities and general trading, precious metals, and increasingly fintech and crypto companies, alongside a broad range of professional services.' },
        ],
      },
      shams: {
        name: 'Sharjah Media City (SHAMS)',
        tagline: 'A low-cost Sharjah free zone aimed at media, creative and e-commerce founders.',
        overview: 'SHAMS (Sharjah Media City) is a budget-friendly free zone focused on media, creative, e-commerce and consultancy businesses. Located in Sharjah, close to Dubai, it offers some of the lowest-cost licence packages in the UAE with 100% foreign ownership.',
        facts: [
          { label: 'Ownership', value: '100% foreign' },
          { label: 'Best for', value: 'Media, creative, e-commerce' },
          { label: 'Profile', value: 'Low cost' },
          { label: 'Location', value: 'Sharjah' },
        ],
        faqs: [
          { question: 'Is SHAMS a good option for a small budget?', answer: 'Yes. SHAMS is known for affordable licence packages and is a common choice for freelancers, media and creative businesses that want low entry costs while staying close to Dubai.' },
        ],
      },
      rakez: {
        name: 'RAKEZ',
        tagline: 'A large, cost-effective Ras Al Khaimah zone for industrial, trading and SME businesses.',
        overview: 'The Ras Al Khaimah Economic Zone (RAKEZ) is a large, competitively priced zone popular for manufacturing, industrial and trading companies, as well as SMEs and freelancers. It offers warehouse and land options alongside standard licences, with 100% foreign ownership.',
        facts: [
          { label: 'Ownership', value: '100% foreign' },
          { label: 'Best for', value: 'Industrial, manufacturing, trading' },
          { label: 'Facilities', value: 'Warehouses & land available' },
          { label: 'Location', value: 'Ras Al Khaimah' },
        ],
        faqs: [
          { question: 'Can I set up a factory or warehouse with RAKEZ?', answer: 'Yes. RAKEZ offers industrial land, warehouses and facilities in addition to service and trading licences, making it a common choice for manufacturing and logistics businesses.' },
        ],
      },
      dafza: {
        name: 'Dubai Airport Free Zone (DAFZA)',
        tagline: 'A premium airport-side zone for logistics, aviation and high-value trade.',
        overview: 'DAFZA sits beside Dubai International Airport and is favoured by logistics, aviation, electronics and high-value trading companies that benefit from proximity to cargo facilities. It is a premium zone with strong infrastructure and 100% foreign ownership.',
        facts: [
          { label: 'Ownership', value: '100% foreign' },
          { label: 'Best for', value: 'Logistics, aviation, trading' },
          { label: 'Profile', value: 'Premium, airport-side' },
          { label: 'Location', value: 'Dubai (near DXB)' },
        ],
        faqs: [
          { question: 'Who benefits most from DAFZA?', answer: 'Companies dealing in air cargo, logistics, aviation services and high-value goods benefit from DAFZA’s location next to Dubai International Airport and its premium infrastructure.' },
        ],
      },
      'dubai-internet-city': {
        name: 'Dubai Internet City',
        tagline: "The region's leading technology cluster for ICT, software and digital media.",
        overview: 'Dubai Internet City, part of the TECOM group, is the region’s flagship technology cluster, home to global tech firms and startups in software, ICT, AI and digital media. It offers a prestigious ecosystem and networking, and is best suited to established or funded technology companies.',
        facts: [
          { label: 'Ownership', value: '100% foreign' },
          { label: 'Best for', value: 'Technology, ICT, digital media' },
          { label: 'Ecosystem', value: 'Global tech community (TECOM)' },
          { label: 'Location', value: 'Dubai' },
        ],
        faqs: [
          { question: 'Is Dubai Internet City only for large tech firms?', answer: 'It hosts many global technology companies, but also startups and scale-ups that want to be inside a recognised tech ecosystem. It tends to suit funded or established firms rather than the lowest-cost setups.' },
        ],
      },
      jafza: {
        name: 'Jebel Ali Free Zone (JAFZA)',
        tagline: 'One of the largest UAE free zones, built around Jebel Ali Port.',
        overview: 'JAFZA, next to Jebel Ali Port, is one of the largest and oldest free zones in the UAE and a hub for large-scale trading, manufacturing and logistics. Its access to a major deep-water port makes it ideal for import/export and distribution businesses handling physical goods.',
        facts: [
          { label: 'Ownership', value: '100% foreign' },
          { label: 'Best for', value: 'Trade, manufacturing, logistics' },
          { label: 'Anchor', value: 'Jebel Ali Port' },
          { label: 'Location', value: 'Dubai (Jebel Ali)' },
        ],
        faqs: [
          { question: 'Why choose JAFZA for a trading business?', answer: 'JAFZA’s location beside Jebel Ali Port gives direct access to deep-water shipping, making it a strong base for import/export, distribution and manufacturing companies that move physical goods at scale.' },
        ],
      },
      ajman: {
        name: 'Ajman Free Zone',
        tagline: 'An affordable zone for SMEs, trading and light-industrial businesses.',
        overview: 'Ajman Free Zone is a low-cost option for SMEs, trading and light-industrial businesses, located in the emirate of Ajman close to Dubai and Sharjah. It offers affordable packages, 100% foreign ownership and a simple setup process.',
        facts: [
          { label: 'Ownership', value: '100% foreign' },
          { label: 'Best for', value: 'SMEs, trading, light industry' },
          { label: 'Profile', value: 'Low cost' },
          { label: 'Location', value: 'Ajman' },
        ],
        faqs: [
          { question: 'Is Ajman Free Zone suitable for a startup on a budget?', answer: 'Yes. Ajman Free Zone is one of the more affordable UAE options and is popular with small businesses and startups that want low setup costs while staying near Dubai and Sharjah.' },
        ],
      },
      adgm: {
        name: 'Abu Dhabi Global Market (ADGM)',
        tagline: 'An international financial centre governed by English common law.',
        overview: 'ADGM, on Al Maryah Island in Abu Dhabi, is an international financial centre operating under an independent English common-law framework. It is the preferred base for financial services, fund managers, family offices, fintech and holding companies that want a globally recognised, well-regulated jurisdiction.',
        facts: [
          { label: 'Ownership', value: '100% foreign' },
          { label: 'Best for', value: 'Finance, funds, holding, fintech' },
          { label: 'Legal framework', value: 'English common law' },
          { label: 'Location', value: 'Abu Dhabi (Al Maryah Island)' },
        ],
        faqs: [
          { question: 'What makes ADGM different from other free zones?', answer: 'ADGM applies its own English common-law framework and independent courts, which is attractive to financial services firms, fund managers and holding companies that want a globally familiar legal environment.' },
          { question: 'Is ADGM only for financial companies?', answer: 'Financial services are its core, but ADGM is also widely used for holding companies, family offices, special purpose vehicles and fintech businesses that value its legal framework.' },
        ],
      },
    },
    list: [
      { slug: 'ifza', name: 'IFZA', code: 'IFZA', blurb: 'Cost-effective Dubai licence' },
      { slug: 'meydan', name: 'Meydan', code: 'MEY', blurb: 'Prestige Dubai address, fast setup' },
      { slug: 'dmcc', name: 'DMCC', code: 'DMCC', blurb: 'Global trade & commodities hub' },
      { slug: 'shams', name: 'SHAMS', code: 'SHAMS', blurb: 'Low-cost media & creative zone' },
      { slug: 'rakez', name: 'RAKEZ', code: 'RAKEZ', blurb: 'Industrial & trading, low cost' },
      { slug: 'dafza', name: 'DAFZA', code: 'DAFZA', blurb: 'Airport-side logistics & trade' },
      { slug: 'dubai-internet-city', name: 'Dubai Internet City', code: 'DIC', blurb: 'Tech & ICT ecosystem' },
      { slug: 'jafza', name: 'JAFZA', code: 'JAFZA', blurb: 'Port-based trade & manufacturing' },
      { slug: 'ajman', name: 'Ajman', code: 'AFZ', blurb: 'Affordable SME & trading zone' },
      { slug: 'adgm', name: 'ADGM', code: 'ADGM', blurb: 'Common-law financial centre' },
    ],
    default: {
      overview: 'This free zone follows the general UAE model: 100% foreign ownership, a business licence tied to specific activities, and a registered address or flexi-desk within the zone. Companies fall under UAE Corporate Tax at 9% on taxable profit above AED 375,000, though qualifying free-zone income can be taxed at 0%. VAT registration applies once turnover crosses AED 375,000.',
      facts: [
        { label: 'Ownership', value: '100% foreign' },
        { label: 'Licence', value: 'Activity-based' },
        { label: 'Office', value: 'Flexi-desk or physical' },
        { label: 'Corporate Tax', value: '9% (0% on qualifying income)' },
      ],
      faqs: [
        { question: 'Do UAE free zone companies pay corporate tax?', answer: 'Free zones fall under the 9% UAE Corporate Tax regime, but a "qualifying free zone person" earning qualifying income can benefit from a 0% rate if the conditions are met. Advice on qualifying income is worth taking early.' },
        { question: 'Can a free zone company trade on the UAE mainland?', answer: 'Free zone companies are generally restricted to operating within their zone and internationally. Selling directly into the mainland usually requires a mainland distributor, agent or branch.' },
      ],
    },
  },

  uk: {
    label: 'United Kingdom', kind: 'Nation', image: UK_IMAGE,
    basePath: '/country/uk/regions', countryPath: '/country/uk',
    intro: 'The UK shares one company law across four nations. Here is what changes — and what stays the same — depending on where you base your company.',
    linkHeading: { eyebrow: 'Across the UK', title: 'One company law, four nations.', text: 'Company law is UK-wide, but legal jurisdiction and where you register vary. See what changes across England, Scotland, Wales and Northern Ireland.' },
    guide: {
      process: [
        { title: 'Structure & name', text: 'Decide between a private limited company (Ltd) or LLP and check name availability at Companies House.' },
        { title: 'Prepare details', text: 'Gather directors, shareholders and people with significant control (PSCs), a UK registered office and your SIC activity codes.' },
        { title: 'Identity checks', text: 'Complete the identity verification now required for directors and people with significant control.' },
        { title: 'Incorporate', text: 'File with Companies House — a standard incorporation is usually approved within 24 hours.' },
        { title: 'Tax & first filings', text: 'Register for Corporation Tax (and VAT/PAYE if needed) and diarise your accounts and confirmation statement.' },
      ],
      essentialsTitle: 'What you will need',
      essentials: [
        'At least one director and one shareholder',
        'A UK registered office address',
        'Details of people with significant control (PSCs)',
        'Standard Industrial Classification (SIC) codes for your activity',
      ],
      supportTitle: 'How StartEase helps',
      support: [
        'Incorporating your Ltd or LLP correctly the first time',
        'Providing a registered office and handling identity checks',
        'Corporation Tax, VAT and PAYE registration',
        'Accounts, confirmation statements and ongoing filing',
      ],
      costsNote: 'The government incorporation fee is modest; the larger considerations are your registered office, accounting and any VAT or payroll needs. We quote a clear annual figure so there are no surprises.',
      faqs: [
        { question: 'Can a non-resident set up a UK company?', answer: 'Yes. There is no residency requirement for directors or shareholders; you do need a UK registered office address and must complete the identity checks now required.' },
        { question: 'How quickly can I incorporate in the UK?', answer: 'A standard online incorporation at Companies House is typically approved within 24 hours once your details and identity checks are complete.' },
      ],
    },
    regions: {
      england: {
        name: 'England',
        tagline: 'The England & Wales jurisdiction — the default base for most UK companies.',
        overview: 'England is home to London and the majority of UK companies. Companies incorporated here sit in the England & Wales legal jurisdiction and register with Companies House. Company law (the Companies Act 2006) is the same across the UK, so the practical differences are mainly about location, courts and professional networks rather than the incorporation process itself.',
        facts: [
          { label: 'Jurisdiction', value: 'England & Wales' },
          { label: 'Registry', value: 'Companies House (Cardiff)' },
          { label: 'Company law', value: 'Companies Act 2006 (UK-wide)' },
          { label: 'Main hub', value: 'London' },
        ],
        faqs: [
          { question: 'Is incorporating in England different from the rest of the UK?', answer: 'The Companies Act 2006 applies across the UK, so the process is essentially the same. England companies sit in the England & Wales legal jurisdiction, which mainly affects courts and property law rather than day-to-day company compliance.' },
          { question: 'Where do England companies file?', answer: 'With Companies House. Records for England & Wales are maintained at the Cardiff office, where annual accounts and a confirmation statement are filed.' },
        ],
      },
      scotland: {
        name: 'Scotland',
        tagline: 'A separate legal system under the same UK company law.',
        overview: 'Scotland has its own distinct legal system (Scots law), and Companies House maintains a separate register in Edinburgh. The Companies Act 2006 still applies, so incorporation and filing are broadly the same, but some areas — such as how floating charges and heritable (property) rights work — differ from England. Founders based in or trading mainly with Scotland often incorporate here.',
        facts: [
          { label: 'Jurisdiction', value: 'Scotland (Scots law)' },
          { label: 'Registry', value: 'Companies House (Edinburgh)' },
          { label: 'Company law', value: 'Companies Act 2006 (UK-wide)' },
          { label: 'Main hub', value: 'Edinburgh, Glasgow' },
        ],
        faqs: [
          { question: 'Does Scotland have different company law?', answer: 'Company law is UK-wide under the Companies Act 2006, so the core rules match. Scotland’s separate legal system mainly affects contracts, property and matters such as floating charges, not the standard incorporation and filing process.' },
          { question: 'Where are Scottish companies registered?', answer: 'At the Companies House office in Edinburgh, which holds the register for companies incorporated in Scotland.' },
        ],
      },
      wales: {
        name: 'Wales',
        tagline: 'Part of the England & Wales jurisdiction, with bilingual options.',
        overview: 'Wales shares the England & Wales legal jurisdiction and the same Companies House register, so incorporation works exactly as it does in England. Companies can choose to identify as Welsh companies and file certain documents bilingually in Welsh and English. Cardiff is the main commercial and administrative hub.',
        facts: [
          { label: 'Jurisdiction', value: 'England & Wales' },
          { label: 'Registry', value: 'Companies House (Cardiff)' },
          { label: 'Company law', value: 'Companies Act 2006 (UK-wide)' },
          { label: 'Main hub', value: 'Cardiff' },
        ],
        faqs: [
          { question: 'Is a Welsh company different from an English one?', answer: 'Legally they sit in the same England & Wales jurisdiction and share one register. The main distinction is that companies can register as Welsh companies and file some documents bilingually in Welsh and English.' },
        ],
      },
      'northern-ireland': {
        name: 'Northern Ireland',
        tagline: 'A separate legal jurisdiction with its own Companies House office.',
        overview: 'Northern Ireland is a distinct legal jurisdiction with a Companies House office in Belfast. UK company law applies, but Northern Ireland’s position can give certain goods-based businesses access to both the UK internal market and the EU single market under current arrangements. Belfast is the main commercial centre.',
        facts: [
          { label: 'Jurisdiction', value: 'Northern Ireland' },
          { label: 'Registry', value: 'Companies House (Belfast)' },
          { label: 'Company law', value: 'Companies Act 2006 (UK-wide)' },
          { label: 'Main hub', value: 'Belfast' },
        ],
        faqs: [
          { question: 'Where do Northern Ireland companies register?', answer: 'At the Companies House office in Belfast, which maintains the register for companies incorporated in Northern Ireland.' },
          { question: 'Does Northern Ireland offer any trade advantages?', answer: 'Under current arrangements, Northern Ireland has a unique position that can give certain goods-based businesses access to both the UK and EU markets. Eligibility depends on the sector and the rules in force, so specialist advice is sensible.' },
        ],
      },
    },
    list: [
      { slug: 'england', name: 'England', code: 'ENG', blurb: 'England & Wales jurisdiction, London hub' },
      { slug: 'scotland', name: 'Scotland', code: 'SCO', blurb: 'Separate legal system, Edinburgh registry' },
      { slug: 'wales', name: 'Wales', code: 'WAL', blurb: 'England & Wales jurisdiction, bilingual' },
      { slug: 'northern-ireland', name: 'Northern Ireland', code: 'NIR', blurb: 'Separate jurisdiction, Belfast registry' },
    ],
    default: {
      overview: 'This part of the UK follows the same company law (Companies Act 2006) as the rest of the country. Incorporation is through Companies House, and a UK registered office and the standard annual filings — accounts and a confirmation statement — apply wherever in the UK you are based.',
      facts: [
        { label: 'Registry', value: 'Companies House' },
        { label: 'Company law', value: 'Companies Act 2006 (UK-wide)' },
        { label: 'Filings', value: 'Accounts + confirmation statement' },
      ],
      faqs: [
        { question: 'Does it matter which UK nation I incorporate in?', answer: 'For most companies the process and obligations are the same UK-wide. The choice mainly affects legal jurisdiction, courts and local networks rather than day-to-day compliance.' },
      ],
    },
  },

  singapore: {
    label: 'Singapore', kind: 'Business district', image: SG_IMAGE,
    basePath: '/country/singapore/zones', countryPath: '/country/singapore',
    intro: 'Singapore is a single jurisdiction, but its districts specialise. Here is where different industries cluster and what each area offers founders.',
    linkHeading: { eyebrow: 'Where industries cluster', title: 'Find your Singapore base.', text: 'Singapore incorporates nationally, but each district has a distinct industry profile. Explore where finance, tech, manufacturing and logistics concentrate.' },
    guide: {
      process: [
        { title: 'Structure & name', text: 'Reserve your company name with ACRA and confirm a private limited company is the right fit.' },
        { title: 'Appoint people', text: 'Appoint at least one resident director, a qualified company secretary and confirm your registered address.' },
        { title: 'Incorporate with ACRA', text: 'File the incorporation — approval is usually same-day once documents and KYC are complete.' },
        { title: 'Bank & licences', text: 'Open a corporate bank account and apply for any activity-specific licences you need.' },
        { title: 'Tax & first filings', text: 'Register for GST if required and set up your first ECI, annual return and tax filings.' },
      ],
      essentialsTitle: 'What you will need',
      essentials: [
        'At least one Singapore-resident director',
        'A qualified company secretary, appointed within six months',
        'A local registered office address',
        'Paid-up capital (from S$1) and shareholder details',
      ],
      supportTitle: 'How StartEase helps',
      support: [
        'Providing a resident director and company secretary where needed',
        'Incorporating with ACRA and handling KYC',
        'Corporate tax and GST registration',
        'Annual returns, ECI and ongoing compliance',
      ],
      costsNote: 'The main recurring costs are the corporate secretary, a nominee resident director if you need one, and accounting. New companies also benefit from Singapore’s start-up tax exemptions. We set out the full annual cost up front.',
      faqs: [
        { question: 'Do I need a local director in Singapore?', answer: 'Yes. Every Singapore company must have at least one director ordinarily resident in Singapore. If you do not have one, a nominee resident director can be arranged.' },
        { question: 'How fast is Singapore incorporation?', answer: 'Incorporation with ACRA is often completed the same day once name approval, KYC and documents are in place.' },
      ],
    },
    regions: {
      'one-north': {
        name: 'One-North',
        tagline: "Singapore's R&D and deep-tech district, home to Biopolis and Fusionopolis.",
        overview: 'One-north, in the Buona Vista area, is Singapore’s flagship research and innovation district. Its Biopolis and Fusionopolis clusters host biomedical, infocomm, media and deep-tech companies alongside universities and research institutes. It is a natural base for startups and R&D-focused firms that want to be close to talent, incubators and agencies such as A*STAR.',
        facts: [
          { label: 'Best for', value: 'R&D, biotech, deep tech' },
          { label: 'Key clusters', value: 'Biopolis, Fusionopolis' },
          { label: 'Ecosystem', value: 'Startups, universities, A*STAR' },
          { label: 'Location', value: 'Buona Vista (west-central)' },
        ],
        faqs: [
          { question: 'What kind of companies are based in one-north?', answer: 'Mostly biomedical, technology, media and research-driven companies, along with startups and the institutes and universities that support them.' },
          { question: 'Do I have to be in one-north to run an R&D company in Singapore?', answer: 'No. You can incorporate and operate anywhere in Singapore. One-north simply offers a dense innovation ecosystem and proximity to research partners and grants.' },
        ],
      },
      'marina-bay': {
        name: 'Marina Bay & CBD',
        tagline: "Singapore's financial heart, home to banks, funds and regional HQs.",
        overview: 'Marina Bay and the surrounding Central Business District form Singapore’s financial core, hosting global banks, asset managers, family offices and regional headquarters. Companies in financial services here operate close to the Monetary Authority of Singapore (MAS) and the advisers that support licensing and fundraising.',
        facts: [
          { label: 'Best for', value: 'Finance, funds, regional HQs' },
          { label: 'Regulator', value: 'Monetary Authority of Singapore' },
          { label: 'Profile', value: 'Premium grade-A offices' },
          { label: 'Location', value: 'Downtown Core' },
        ],
        faqs: [
          { question: 'Is Marina Bay only for financial firms?', answer: 'It is dominated by finance and headquarters functions, but many professional services and corporate offices are based there too. It is a prestige business address rather than a regulated zone.' },
        ],
      },
      jurong: {
        name: 'Jurong',
        tagline: 'The industrial and advanced-manufacturing heartland in the west.',
        overview: 'Jurong, including the Jurong Innovation District, is Singapore’s manufacturing and industrial base, covering advanced manufacturing, engineering, chemicals and logistics. Managed largely by JTC, it offers industrial land, factory space and proximity to Singapore’s western port and supply chains.',
        facts: [
          { label: 'Best for', value: 'Manufacturing, engineering, logistics' },
          { label: 'Managed by', value: 'JTC Corporation' },
          { label: 'Facilities', value: 'Industrial land & factories' },
          { label: 'Location', value: 'West Singapore' },
        ],
        faqs: [
          { question: 'What is the Jurong Innovation District?', answer: 'A planned district bringing together advanced manufacturing, research and industry to support the next generation of production and engineering companies in Singapore.' },
        ],
      },
      changi: {
        name: 'Changi',
        tagline: 'Aviation, aerospace and air-logistics hub around Changi Airport.',
        overview: 'The Changi area, anchored by Changi Airport and Changi Business Park, is Singapore’s centre for aviation, aerospace, air cargo and logistics, alongside data and technology firms in the business park. It suits companies whose operations depend on air connectivity and the fast movement of goods.',
        facts: [
          { label: 'Best for', value: 'Aviation, aerospace, logistics' },
          { label: 'Anchor', value: 'Changi Airport & Business Park' },
          { label: 'Profile', value: 'Air cargo & connectivity' },
          { label: 'Location', value: 'East Singapore' },
        ],
        faqs: [
          { question: 'Why base a business near Changi?', answer: 'Proximity to one of the world’s busiest air hubs benefits aviation, aerospace, e-commerce and logistics companies that rely on fast air freight and international travel.' },
        ],
      },
      tuas: {
        name: 'Tuas',
        tagline: 'Maritime and port-centric zone, home to the new Tuas Mega Port.',
        overview: 'Tuas, at Singapore’s far west, is the site of the Tuas Mega Port — set to become one of the world’s largest container terminals — and a growing base for maritime, shipping, logistics and heavy-industry companies. It is geared to businesses handling large volumes of physical goods and port-linked services.',
        facts: [
          { label: 'Best for', value: 'Maritime, shipping, heavy industry' },
          { label: 'Anchor', value: 'Tuas Mega Port' },
          { label: 'Profile', value: 'Port & logistics' },
          { label: 'Location', value: 'Far west Singapore' },
        ],
        faqs: [
          { question: 'What is the Tuas Mega Port?', answer: 'A large-scale port being consolidated at Tuas that will handle Singapore’s container throughput, making the area a focal point for maritime and logistics businesses.' },
        ],
      },
    },
    list: [
      { slug: 'one-north', name: 'One-North', code: 'R&D', blurb: 'Deep tech, biomed, startups' },
      { slug: 'marina-bay', name: 'Marina Bay', code: 'FIN', blurb: 'Finance, funds & regional HQs' },
      { slug: 'jurong', name: 'Jurong', code: 'MFG', blurb: 'Manufacturing & industry' },
      { slug: 'changi', name: 'Changi', code: 'AIR', blurb: 'Aviation & air logistics' },
      { slug: 'tuas', name: 'Tuas', code: 'PORT', blurb: 'Maritime & the mega port' },
    ],
    default: {
      overview: 'Wherever in Singapore you base your company, incorporation is through ACRA and the same rules apply: a resident director, a company secretary and a registered address. Location mainly affects your operating environment and industry ecosystem rather than your compliance obligations.',
      facts: [
        { label: 'Registry', value: 'ACRA' },
        { label: 'Requirement', value: 'Resident director + secretary' },
        { label: 'Corporate tax', value: '17% (with exemptions)' },
      ],
      faqs: [
        { question: 'Does my Singapore business location change my obligations?', answer: 'No. Incorporation, tax and filing requirements are national. Choosing a district affects networks, facilities and industry proximity rather than your compliance.' },
      ],
    },
  },

  // US state pages are served by the existing /usa/states template, so no region grid here.
  us: {
    label: 'United States', kind: 'State', image: US_IMAGE,
    basePath: '/usa/states', countryPath: '/usa',
    intro: 'US state pages are served by the existing /usa/states template.',
    regions: {}, list: [], default: { overview: '', facts: [], faqs: [] },
  },
};

export const getRegionData = (market, slug) => {
  const cfg = REGION_CONFIG[market];
  if (!cfg) return null;
  const region = cfg.regions[slug] || cfg.default;
  return { cfg, region, isDefault: !cfg.regions[slug], name: region.name || deslug(slug) };
};

const deslug = (s) => (s || '').split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

export const SITE_ORIGIN = process.env.REACT_APP_PUBLIC_SITE_URL;
